import React, { Fragment, PureComponent } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import axios from 'axios';
import { get } from 'lodash-es';
import ReactLoading from 'react-loading';
import postalCodes from 'postal-codes-js';

import CustomInputField from '../../utilities/form-components/CustomInputField';

class EditLocation extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      errorAlert: null,
      fetchedStates: false,
      formErrors: {},
      formValues: {},
      houseRef: '',
      loading: true,
      locationLength: 0,
      primaryChecked: false,
      showModal: false,
      stateLabel: 'State/Province'
    };
  }

  // Props changed from the main list - load the appropriate location into the form
  static getDerivedStateFromProps(props, state) {
    const { showModal } = props;
    if (showModal !== state.showModal) {
      const { values } = props;

      /**
       * This is sort of like reverse redux - map props from higher component to state :)
       * The empty quotes are for the 'add' and default/no value case
       */
      return {
        showModal,
        formErrors: {},
        formValues: {
          countryCode: get(values, 'countryCode', ''),
          primary: get(values, 'primary', false),
          addressFax: get(values, 'addressFax', ''),
          addressPhone: get(values, 'addressPhone', ''),
          addressStreet1: get(values, 'addressStreet1', ''),
          addressStreet2: get(values, 'addressStreet2', ''),
          addressCity: get(values, 'addressCity', ''),
          postalCode: get(values, 'postalCode', ''),
          stateCode: get(values, 'stateCode', ''),
          province: get(values, 'province', ''),
          facilityName: get(values, 'facilityName', ''),
          email: get(values, 'email', '')
        },
        stateLabel: 'State/Province'
      };
    }

    return null;
  }

  // This CDU function will fetch the states one time if the user has a pre-selected countryCode.
  componentDidUpdate() {
    const countryCode = get(this.state, 'formValues.countryCode', null);
    /**
     * This resolves a bug where clicking 'edit' the second time, would cause the
     * state field to not fetch.
     */
    if (!this.state.showModal) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ fetchedStates: false });
    } else if (countryCode && !this.state.fetchedStates) {
      const { countries } = this.state;
      this.fetchStates(countryCode, countries);
    }
  }

  /**
   * When the component mounts - we have to first fetch the list of countries to show from
   * the BE. We then want to put the popular countries at the top of the list.
   *
   * If the user had selected a popular country, we then want to get the list of states/provinces
   */
  componentDidMount() {
    this.fetchCountries();
  }

  // Calling the parent components handle location modal function to hide the modal.
  hideModal = () => {
    this.props.handleLocationModal(false, this.props.index);
  };

  /** *********************************************************
   * Start Country/State Logic
   ********************************************************** */
  /**
   * fetchCountries is called one time, when the component mounts (aka on the locations list).
   * The purpose of this function is to set the country list in state, and to move the
   * important countries to the top of the select dropdown.
   */
  fetchCountries = async () => {
    try {
      const countryResponse = await axios('/app/country');
      const countries = get(countryResponse, 'data.data.countries');

      // List of country codes that we want to bring to the top
      const importantCountries = ['UK', 'US', 'CA', 'AU'];

      const firstCountries = countries.filter(
        country => importantCountries.indexOf(country.countryCode) >= 0
      );

      const remainingCountries = countries.filter(
        country => importantCountries.indexOf(country.countryCode) === -1
      );

      const filteredCountries = [...firstCountries, ...remainingCountries];

      this.setState({
        countries: filteredCountries,
        loading: false
      });
    } catch (error) {
      console.log(error);
      this.setState({ loading: false });
    }
  };

  /**
   * fetchStates is called in two instances
   *
   * 1. If the props change - meaning a user clicked the edit button - in this case we need to
   * see if they already had a selected country/state and get the appropriate list
   * based on the country.
   *
   * 2. If the user selects a new country - so we need to update the list.
   *
   * For both cases we want to blank out the validation, state/province field,
   * and the postal code field so that the user enters new information.
   */
  fetchStates = async (countryCode, countries) => {
    /**
     * The user selected the placeholder value, just blank out the data and delete
     * validation errors, no need to make an API req
     */
    if (!countryCode) {
      const formErrors = { ...this.state.formErrors };
      delete formErrors.countryCode;
      delete formErrors.stateCode;
      delete formErrors.province;
      delete formErrors.postalCode;

      this.setState({
        formErrors,
        formValues: {
          ...this.state.formValues,
          countryCode: '',
          stateCode: '',
          province: '',
          postalCode: ''
        },
        stateLabel: 'State/Province'
      });
      return;
    }

    try {
      const stateResponse = await axios(`/app/states?country=${countryCode}`);
      const states = get(stateResponse, 'data.data.states');

      const stateLabel = this.getStateLabel(countryCode);

      /**
       * This is the initial loading state, we essentially need to fetch the states if
       * the user has their country already selected.
       *
       * Note this time we set countries in state, and loading to false.
       */
      if (countries) {
        this.setState({
          fetchedStates: true,
          states,
          stateLabel,
          countries,
          loading: false,
          formValues: {
            ...this.state.formValues,
            countryCode
          }
        });
      } else {
        /**
         * Otherwise - we are re-fetching states based on the change of the country select box
         *
         * Note that here we blank out the state field so the user has to re-select a state and
         * postal code.
         */
        const formErrors = { ...this.state.formErrors };
        delete formErrors.countryCode;
        delete formErrors.stateCode;
        delete formErrors.province;
        delete formErrors.postalCode;

        this.setState({
          states,
          stateLabel,
          formErrors,
          formValues: {
            ...this.state.formValues,
            countryCode,
            stateCode: '',
            province: '',
            postalCode: ''
          }
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  // First re-fetch the states/provinces/etc. from BE and then update country in component state
  handleSelectCountry = e => {
    const { value: countryCode } = e.target;
    this.fetchStates(countryCode);
  };

  // Update State in State :)
  handleSelectState = e => {
    const { value: stateCode } = e.target;
    const formErrors = { ...this.state.formErrors };

    if (stateCode) {
      delete formErrors.stateCode;
    }

    this.setState({
      formErrors,
      formValues: {
        ...this.state.formValues,
        stateCode
      }
    });
  };

  // Update the state label when a country is changed
  getStateLabel = countryCode => {
    let stateLabel = '';
    switch (countryCode) {
      case 'US':
        stateLabel = 'State';
        break;
      case 'CA':
        stateLabel = 'Province';
        break;
      case 'AU':
        stateLabel = 'State/Territory';
        break;
      case 'UK':
        stateLabel = 'County';
        break;
      default:
        stateLabel = 'State/Province';
        break;
    }
    return stateLabel;
  };

  /** *********************************************************
   * End Country/State Logic
   ********************************************************** */
  /** *********************************************************
   * Start Validation
   ********************************************************** */
  validateRequired = e => {
    if (!e.target.value) {
      this.setState({
        ...this.state,
        formErrors: {
          ...this.state.formErrors,
          [e.target.name]: `${e.target.getAttribute('friendlyname')} is required`
        }
      });
    }
  };

  validatePostalCode = e => {
    const {
      formValues: { countryCode }
    } = this.state;

    if (!countryCode) {
      this.setState({
        ...this.state,
        formErrors: {
          ...this.state.formErrors,
          [e.target.name]: 'Please select a country first'
        }
      });
    } else {
      const postalCode = e.target.value;

      /**
       * This is a temporary fix (HACK) because we hard copy a country list in our database.
       * This means our country list in our database was probably correct in 1980 but as of
       * writing this comment, it is 2019.  In 2019 the correct country code for the United
       * Kingdom is GB, not UK.  Since the postal code validation library is correct, we want
       * to provide correct input to the postal code validation library, and incorrect data to
       * our database which was last updated before I was born.
       */
      const isPostalCodeValidMessage = postalCodes.validate(
        countryCode === 'UK' ? 'GB' : countryCode,
        postalCode
      );

      if (isPostalCodeValidMessage !== true) {
        this.setState({
          ...this.state,
          formErrors: {
            ...this.state.formErrors,
            [e.target.name]: isPostalCodeValidMessage
          }
        });
      }
    }
  };

  /** *********************************************************
   * End Validation
   ********************************************************** */
  /** *********************************************************
   * Start form handler logic
   ********************************************************** */
  handleCheckbox = () => {
    this.setState({
      primaryChecked: !this.state.primaryChecked
    });
  };

  // Dynamically set changed input fields in the state
  handleInputChange = e => {
    const formErrors = { ...this.state.formErrors };
    delete formErrors[e.target.name];

    this.setState({
      formErrors,
      formValues: {
        ...this.state.formValues,
        [e.target.name]: e.target.value
      }
    });
  };

  handleSubmit = async () => {
    const { fetchLocations, houseRef, numLocations, values } = this.props;
    const addressID = get(values, 'id', null);
    const { formValues, primaryChecked } = this.state;

    if (numLocations <= 0) {
      formValues.primary = true;
    } else {
      delete formValues.primary;
      formValues.primary = primaryChecked;
    }

    const formJSON = addressID ? { addressID, ...formValues } : formValues;

    try {
      const postResponse = await axios.post(
        `/app/house/${houseRef}/location`,
        JSON.stringify(formJSON),
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      // Backend all of a sudden started sending this as a 200 instead of an error.  It's an error.
      const errorGoogleAPI = get(postResponse, 'data.data.azhouseAdminError.errorCode') === 302;
      if (errorGoogleAPI) {
        this.setState({
          errorAlert: 'Please verify that your country and postal code are in the correct format.'
        });
      } else {
        const status = get(postResponse, 'data.data.status');
        if (status === 'success') {
          this.hideModal();
          fetchLocations();
        }
      }
    } catch (error) {
      // Probably now un-necessary since BE decided to treat this error as successful
      const azhouseAdminError = get(error, 'response.data.azhouseAdminError', null);
      if (azhouseAdminError) {
        this.setState({
          errorAlert: 'Please verify that your country and postal code are in the correct format.'
        });
      }
    }
  };

  /** *********************************************************
   * End form handler logic
   ********************************************************** */

  render() {
    const { enablePrimary = true, numLocations, showModal, index } = this.props;

    if (!showModal) {
      return <div />;
    }

    const {
      countries,
      errorAlert,
      formErrors,
      formValues: {
        addressStreet1,
        addressStreet2,
        addressCity,
        countryCode,
        email,
        facilityName,
        addressFax,
        primary,
        stateCode,
        province,
        addressPhone,
        postalCode
      },
      loading,
      primaryChecked,
      states,
      stateLabel
    } = this.state;

    const zipCodeLabel = countryCode === 'US' ? 'Zip Code' : 'Postal Code';

    // If required fields are not filled out, or if there are validation errors, disable form submit
    const disableForm = !(
      addressStreet1
      && addressCity
      && countryCode
      && (stateCode || province)
      && postalCode
      && Object.keys(formErrors).length === 0
    );

    return (
      <Modal className="form-modal location-modal-width" isOpen={showModal} toggle={this.hideModal}>
        {/* Had to overwrite ModalHeader because it forces an h5, where we want an h1 */}
        <ModalHeader className="pb-0" toggle={this.hideModal} tag="h1">
          {index === -1 ? 'Add' : 'Edit'} Location
        </ModalHeader>
        <ModalBody className="pt-3">
          {loading ? (
            <ReactLoading className="loading-spinner" type="spin" color="black" />
          ) : (
            <Form>
              {errorAlert && <Alert color="danger">{errorAlert}</Alert>}

              {enablePrimary && (
                <FormGroup className="pb-1" check>
                  <Label className={primary ? 'disabled-label' : ''} check>
                    <Input
                      type="checkbox"
                      disabled={primary || numLocations === 0}
                      checked={primary || numLocations === 0 || primaryChecked}
                      onChange={e => this.handleCheckbox(e)}
                    />
                    <p>
                      Primary Location{' '}
                      {primary ? (
                        <i>- edit or add another location to designate a new primary location</i>
                      ) : (
                        ''
                      )}
                    </p>
                  </Label>
                </FormGroup>
              )}

              <FormGroup>
                <CustomInputField
                  friendlyname="Facility Name"
                  id="facilityName"
                  maxLength="75"
                  onChange={e => this.handleInputChange(e)}
                  value={facilityName}
                />
              </FormGroup>

              <FormGroup>
                <CustomInputField
                  friendlyname="Street Address 1"
                  hasError={formErrors.addressStreet1}
                  id="addressStreet1"
                  isRequired={true}
                  maxLength="50"
                  onBlur={e => this.validateRequired(e)}
                  onChange={e => this.handleInputChange(e)}
                  value={addressStreet1}
                />
              </FormGroup>

              <FormGroup>
                <CustomInputField
                  friendlyname="Street Address 2"
                  id="addressStreet2"
                  maxLength="50"
                  onChange={e => this.handleInputChange(e)}
                  value={addressStreet2}
                />
              </FormGroup>

              <FormGroup>
                <Label for="selectCountry">Country*</Label>
                <Input
                  className={formErrors.countryCode ? 'is-invalid' : ''}
                  type="select"
                  id="selectCountry"
                  friendlyname="Country"
                  name="countryCode"
                  value={countryCode}
                  onBlur={e => this.validateRequired(e)}
                  onChange={e => this.handleSelectCountry(e)}
                >
                  <option value="">Select Country</option>
                  {countries
                    && countries.map(country => (
                      <option key={country.countryCode} value={country.countryCode}>
                        {country.countryName}
                      </option>
                    ))}
                </Input>
                {formErrors.countryCode && (
                  <p className="color-danger my-0">{formErrors.countryCode}</p>
                )}
              </FormGroup>

              <FormGroup>
                <CustomInputField
                  friendlyname="City"
                  hasError={formErrors.addressCity}
                  id="addressCity"
                  isRequired={true}
                  maxLength="50"
                  onBlur={e => this.validateRequired(e)}
                  onChange={e => this.handleInputChange(e)}
                  value={addressCity}
                />
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col>
                    {stateLabel === 'State/Province' ? (
                      <CustomInputField
                        friendlyname={stateLabel}
                        hasError={formErrors.province}
                        id="province"
                        isRequired={true}
                        maxLength="50"
                        onBlur={e => this.validateRequired(e)}
                        onChange={e => this.handleInputChange(e)}
                        value={province}
                      />
                    ) : (
                      <Fragment>
                        <Label for="selectState">{stateLabel}*</Label>
                        <Input
                          className={formErrors.stateCode ? 'is-invalid' : ''}
                          type="select"
                          id="selectState"
                          friendlyname={stateLabel}
                          name="stateCode"
                          value={stateCode}
                          onBlur={e => this.validateRequired(e)}
                          onChange={e => this.handleSelectState(e)}
                        >
                          <option value="">Select {stateLabel}</option>
                          {states
                            && states.length > 0
                            && states.map(state => (
                              <option key={state.stateCode} value={state.stateCode}>
                                {state.stateName}
                              </option>
                            ))}
                        </Input>
                      </Fragment>
                    )}
                    {formErrors.stateCode && (
                      <p className="color-danger my-0">{formErrors.stateCode}</p>
                    )}
                  </Col>
                  <Col>
                    <CustomInputField
                      friendlyname={zipCodeLabel}
                      hasError={formErrors.postalCode}
                      id="postalCode"
                      isRequired={true}
                      maxLength="32"
                      onBlur={e => {
                        this.validatePostalCode(e);
                        this.validateRequired(e);
                      }}
                      onChange={e => this.handleInputChange(e)}
                      value={postalCode}
                    />
                  </Col>
                </Row>
              </FormGroup>

              <FormGroup>
                <Row>
                  <Col>
                    <CustomInputField
                      friendlyname="Phone"
                      id="addressPhone"
                      maxLength="40"
                      onChange={e => this.handleInputChange(e)}
                      value={addressPhone}
                    />
                  </Col>
                  <Col>
                    <CustomInputField
                      friendlyname="Fax"
                      id="addressFax"
                      onChange={e => this.handleInputChange(e)}
                      value={addressFax}
                    />
                  </Col>
                </Row>
              </FormGroup>

              <FormGroup>
                <CustomInputField
                  friendlyname="Email"
                  maxLength="60"
                  id="email"
                  onChange={e => this.handleInputChange(e)}
                  value={email}
                />
              </FormGroup>
            </Form>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="link" onClick={() => this.hideModal()}>
            Cancel
          </Button>
          <Button
            className="cursor-not-allowed mr-3"
            color="primary"
            disabled={disableForm}
            onClick={() => this.handleSubmit()}
          >
            Save Address
          </Button>{' '}
        </ModalFooter>
      </Modal>
    );
  }
}

export default EditLocation;
